import React, { useEffect, useRef } from "react";
import "./Certificate.css";

interface CertificateProps {
  open: boolean;
  onClose: () => void;
  studentName: string;
  courseName: string;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function star(ctx: CanvasRenderingContext2D, cx: number, cy: number, spikes: number, outer: number, inner: number) {
  let rot = -Math.PI / 2;
  const step = Math.PI / spikes;
  ctx.beginPath();
  ctx.moveTo(cx + Math.cos(rot) * outer, cy + Math.sin(rot) * outer);
  for (let i = 0; i < spikes; i++) {
    rot += step; ctx.lineTo(cx + Math.cos(rot) * inner, cy + Math.sin(rot) * inner);
    rot += step; ctx.lineTo(cx + Math.cos(rot) * outer, cy + Math.sin(rot) * outer);
  }
  ctx.closePath();
  ctx.fill();
}

function codeFrom(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return "BC-" + h.toString(36).toUpperCase().padStart(6, "0").slice(0, 6);
}

function draw(canvas: HTMLCanvasElement, studentName: string, courseName: string) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const W = 1200, H = 820, cx = W / 2;
  canvas.width = W; canvas.height = H;

  // fundo
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#0a1428"); bg.addColorStop(1, "#05080f");
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
  const g1 = ctx.createRadialGradient(W * 0.16, -60, 30, W * 0.16, -60, 720);
  g1.addColorStop(0, "rgba(67,127,244,0.22)"); g1.addColorStop(1, "rgba(67,127,244,0)");
  ctx.fillStyle = g1; ctx.fillRect(0, 0, W, H);
  const g2 = ctx.createRadialGradient(W * 0.9, H + 60, 30, W * 0.9, H + 60, 640);
  g2.addColorStop(0, "rgba(99,228,208,0.14)"); g2.addColorStop(1, "rgba(99,228,208,0)");
  ctx.fillStyle = g2; ctx.fillRect(0, 0, W, H);

  // molduras
  ctx.strokeStyle = "rgba(239,196,89,0.55)"; ctx.lineWidth = 2;
  roundRect(ctx, 34, 34, W - 68, H - 68, 24); ctx.stroke();
  ctx.strokeStyle = "rgba(99,228,208,0.35)"; ctx.lineWidth = 1;
  roundRect(ctx, 48, 48, W - 96, H - 96, 18); ctx.stroke();

  // ornamentos de canto (losangos aqua nos 4 cantos internos)
  ctx.fillStyle = "rgba(99,228,208,0.85)";
  const corners = [[62, 62], [W - 62, 62], [62, H - 62], [W - 62, H - 62]];
  for (const [x, y] of corners) {
    ctx.save(); ctx.translate(x, y); ctx.rotate(Math.PI / 4);
    ctx.fillRect(-4, -4, 8, 8); ctx.restore();
  }

  ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";

  // wordmark Blue.Capital
  ctx.font = "700 40px 'Space Grotesk', sans-serif";
  const wPre = ctx.measureText("Blue").width;
  const wDot = ctx.measureText(".").width;
  const wordW = wPre + wDot + ctx.measureText("Capital").width;
  const wx = cx - wordW / 2;
  ctx.textAlign = "left";
  ctx.fillStyle = "#eef3ff"; ctx.fillText("Blue", wx, 132);
  ctx.fillStyle = "#63e4d0"; ctx.fillText(".", wx + wPre, 132);
  ctx.fillStyle = "#eef3ff"; ctx.fillText("Capital", wx + wPre + wDot, 132);
  ctx.textAlign = "center";

  // eyebrow com hairlines douradas
  ctx.fillStyle = "#efc459";
  ctx.font = "500 15px 'IBM Plex Mono', monospace";
  const eb = "C E R T I F I C A D O   D E   C O N C L U S Ã O";
  ctx.fillText(eb, cx, 194);
  const ebW = ctx.measureText(eb).width;
  ctx.strokeStyle = "rgba(239,196,89,0.5)"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(cx - ebW / 2 - 40, 189); ctx.lineTo(cx - ebW / 2 - 14, 189); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(cx + ebW / 2 + 14, 189); ctx.lineTo(cx + ebW / 2 + 40, 189); ctx.stroke();

  // corpo
  ctx.fillStyle = "rgba(157,176,204,0.9)";
  ctx.font = "400 20px 'Manrope', sans-serif";
  ctx.fillText("Este certificado é concedido a", cx, 268);

  ctx.fillStyle = "#ffffff";
  ctx.font = "700 56px 'Space Grotesk', sans-serif";
  ctx.fillText(studentName || "Aluno", cx, 342);

  // flourish sob o nome
  const fl = ctx.createLinearGradient(cx - 150, 0, cx + 150, 0);
  fl.addColorStop(0, "rgba(99,228,208,0)"); fl.addColorStop(0.5, "rgba(99,228,208,0.9)"); fl.addColorStop(1, "rgba(99,228,208,0)");
  ctx.strokeStyle = fl; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(cx - 150, 364); ctx.lineTo(cx + 150, 364); ctx.stroke();

  ctx.fillStyle = "rgba(157,176,204,0.9)";
  ctx.font = "400 20px 'Manrope', sans-serif";
  ctx.fillText("por concluir 100% do programa", cx, 412);

  // nome do programa/curso (quebra se longo)
  ctx.fillStyle = "#7aa6ff";
  ctx.font = "600 34px 'Space Grotesk', sans-serif";
  const maxW = W - 260;
  const words = (courseName || "Formação Blue Capital").split(" ");
  let line = "";
  const lines: string[] = [];
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  lines.slice(0, 2).forEach((ln, i) => ctx.fillText(ln, cx, 466 + i * 42));

  // selo dourado com fita
  const sy = 606, sr = 42;
  ctx.fillStyle = "#b9852a";
  for (const dir of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(cx + dir * 14, sy + sr - 8);
    ctx.lineTo(cx + dir * 30, sy + sr + 52);
    ctx.lineTo(cx + dir * 12, sy + sr + 40);
    ctx.lineTo(cx, sy + sr + 2);
    ctx.closePath(); ctx.fill();
  }
  const sg = ctx.createLinearGradient(cx - sr, sy - sr, cx + sr, sy + sr);
  sg.addColorStop(0, "#f7dc94"); sg.addColorStop(0.5, "#efc459"); sg.addColorStop(1, "#c9962f");
  ctx.fillStyle = sg;
  ctx.beginPath(); ctx.arc(cx, sy, sr, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#0b1526";
  ctx.beginPath(); ctx.arc(cx, sy, sr - 8, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "rgba(247,220,148,0.9)";
  for (let i = 0; i < 28; i++) {
    const a = (i / 28) * Math.PI * 2;
    ctx.beginPath(); ctx.arc(cx + Math.cos(a) * (sr - 4), sy + Math.sin(a) * (sr - 4), 1.1, 0, Math.PI * 2); ctx.fill();
  }
  ctx.fillStyle = "#efc459";
  star(ctx, cx, sy, 5, sr - 18, (sr - 18) / 2.3);

  // rodapé: data + código de verificação
  const date = new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
  ctx.fillStyle = "rgba(157,176,204,0.8)";
  ctx.font = "400 16px 'Manrope', sans-serif";
  ctx.fillText("Emitido em " + date, cx, 726);
  ctx.fillStyle = "rgba(100,116,143,0.9)";
  ctx.font = "500 13px 'IBM Plex Mono', monospace";
  ctx.fillText(codeFrom((studentName || "") + "|" + (courseName || "")) + "  ·  academy.bluecapital.pro", cx, 752);
}

const Certificate: React.FC<CertificateProps> = ({ open, onClose, studentName, courseName }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const render = () => draw(canvas, studentName, courseName);
    if ((document as any).fonts?.ready) (document as any).fonts.ready.then(render);
    else render();
    render();
  }, [open, studentName, courseName]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;

  function handleDownload() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `certificado-${(courseName || "curso").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }

  return (
    <div className="cert-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cert-card" role="dialog" aria-modal="true" aria-label="Certificado de conclusão">
        <button className="cert-close" onClick={onClose} aria-label="Fechar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
        <canvas ref={canvasRef} className="cert-canvas" />
        <div className="cert-actions">
          <button className="cert-download" onClick={handleDownload}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" /></svg>
            Baixar certificado
          </button>
        </div>
      </div>
    </div>
  );
};

export default Certificate;
